import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaCalendarAlt, FaMapMarkerAlt, FaClock, FaUsers, FaFilter, 
  FaPlus, FaSearch, FaTicketAlt, FaHeart, FaShare, FaTimes,
  FaTag, FaLaptop, FaGlassCheers, FaGraduationCap, FaHandshake
} from 'react-icons/fa';
import { useAuth } from '../hooks/useAuth';
import { useEvents } from '../hooks/EventContext';

// Sample event data
const sampleEvents = [
  {
    id: '1',
    title: 'Annual Alumni Networking Gala',
    date: '2024-09-15',
    time: '19:00',
    location: 'Grand Ballroom, Hilton Hotel',
    category: 'Networking',
    description: 'Join us for an evening of networking, reconnecting with old friends, and making new connections. Enjoy gourmet food, drinks, and live entertainment.',
    deadline: '2024-09-01',
    attendees: 250,
    organizer: 'Alumni Association',
    speakers: ['Jane Doe, CEO of TechCorp', 'John Smith, Founder of StartUp Inc.'],
    ticketPrice: '$75',
    dresscode: 'Formal',
    virtual: false,
    image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
  },
  {
    id: '2',
    title: 'Tech Trends 2025: Virtual Symposium',
    date: '2024-10-05',
    time: '10:00',
    location: 'Online (Zoom)',
    category: 'Educational',
    description: 'Explore the future of technology with industry experts. Topics include AI, blockchain, quantum computing, and more.',
    deadline: '2024-10-03',
    attendees: 500,
    organizer: 'University Tech Department',
    speakers: ['Dr. Alan Turing, AI Research Lead', 'Sarah Connor, Cybersecurity Expert'],
    ticketPrice: 'Free',
    virtual: true,
    platform: 'Zoom',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
  },
  {
    id: '3',
    title: 'Career Fair 2024',
    date: '2024-11-10',
    time: '09:00',
    location: 'University Convention Center',
    category: 'Career',
    description: 'Connect with top employers, attend workshops on resume building and interview skills, and explore new career opportunities.',
    deadline: '2024-11-08',
    attendees: 1000,
    organizer: 'University Career Services',
    companies: ['Google', 'Amazon', 'Microsoft', 'Apple', 'Facebook'],
    workshops: ['Resume Building', 'Interview Skills', 'LinkedIn Optimization'],
    ticketPrice: 'Free for students and alumni',
    image: 'https://images.unsplash.com/photo-1560523159-4a9692d222ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
  },
  {
    id: '4',
    title: 'Homecoming Weekend Celebration',
    date: '2024-10-20',
    time: '11:00',
    location: 'University Campus',
    category: 'Social',
    description: 'Relive your college days with a weekend full of activities, including a football game, campus tours, and reunion dinners.',
    deadline: '2024-10-10',
    attendees: 3000,
    organizer: 'Alumni Relations Office',
    activities: ['Football Game', 'Campus Tours', 'Reunion Dinners', 'Concert'],
    ticketPrice: '$50 for weekend pass',
    dresscode: 'Casual (University colors encouraged)',
    image: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
  },
  {
    id: '5',
    title: 'Entrepreneurship Workshop Series',
    date: '2024-08-01',
    time: '14:00',
    location: 'Business School Auditorium',
    category: 'Workshop',
    description: 'A 4-week workshop series covering business plan development, funding strategies, marketing, and legal considerations for startups.',
    deadline: '2024-07-25',
    attendees: 100,
    organizer: 'Center for Entrepreneurship',
    speakers: ['Mark Zuckerberg, Facebook Founder', 'Elon Musk, Tesla CEO'],
    sessions: 4,
    ticketPrice: '$200 for full series',
    materials: 'Laptop required',
    image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
  }
];

const EventCard = ({ event, onRegister }) => {
  const isDeadlinePassed = new Date(event.deadline) < new Date();
  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    const registeredEvents = JSON.parse(localStorage.getItem('registeredEvents') || '[]');
    setIsRegistered(registeredEvents.includes(event.id));
  }, [event.id]);

  return (
    <motion.div 
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-white p-6 rounded-lg shadow-md mb-6 hover:shadow-lg transition-shadow duration-300"
    >
      <div className="flex flex-col md:flex-row">
        <div className="md:w-1/3 mb-4 md:mb-0 md:mr-6">
          <img src={event.image} alt={event.title} className="w-full h-full object-cover rounded-lg" />
        </div>
        <div className="md:w-2/3">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">{event.title}</h3>
              <p className="text-gray-600 flex items-center mt-2">
                <FaCalendarAlt className="mr-2 text-blue-500" /> {event.date} 
                <FaClock className="ml-4 mr-2 text-blue-500" /> {event.time}
              </p>
              <p className="text-gray-600 flex items-center mt-1">
                <FaMapMarkerAlt className="mr-2 text-red-500" /> {event.location}
              </p>
              <p className="text-gray-600 flex items-center mt-1">
                <FaTag className="mr-2 text-green-500" /> {event.ticketPrice}
              </p>
            </div>
            <div className="flex space-x-2">
              <button className="text-red-500 hover:text-red-600 transition-colors duration-300">
                <FaHeart />
              </button>
              <button className="text-blue-500 hover:text-blue-600 transition-colors duration-300">
                <FaShare />
              </button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center">
              <FaTag className="mr-1" /> {event.category}
            </span>
            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center">
              <FaUsers className="mr-1" /> {event.attendees} attendees
            </span>
            {event.virtual && (
              <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm flex items-center">
                <FaLaptop className="mr-1" /> Virtual Event
              </span>
            )}
          </div>
          <p className="mt-4 text-gray-700">{event.description}</p>
          {event.speakers && (
            <div className="mt-2">
              <p className="font-semibold text-gray-700">Speakers:</p>
              <ul className="list-disc list-inside text-gray-600">
                {event.speakers.map((speaker, index) => (
                  <li key={index}>{speaker}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-4 flex justify-between items-center">
            <span className="text-sm text-gray-500">Registration deadline: {event.deadline}</span>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-4 py-2 rounded flex items-center ${
                isDeadlinePassed || isRegistered
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600'
              } text-white transition duration-300`}
              onClick={() => !isDeadlinePassed && !isRegistered && onRegister(event)}
              disabled={isDeadlinePassed || isRegistered}
            >
              <FaTicketAlt className="mr-2" />
              {isRegistered ? 'Registered' : isDeadlinePassed ? 'Registration Closed' : 'Register'}
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const CreateEventForm = ({ onClose, onSubmit }) => {
  const [eventData, setEventData] = useState({
    title: '',
    date: '',
    time: '',
    location: '',
    category: '',
    description: '',
    deadline: '',
    ticketPrice: '',
    virtual: false,
    speakers: '',
    attendeeLimit: ''
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEventData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(eventData);
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-lg flex flex-col"
    >
      <div className="p-6 flex-grow overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Create New Event</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaTimes size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
              Event Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={eventData.title}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter event title"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="date">
                Date
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={eventData.date}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="time">
                Time
              </label>
              <input
                type="time"
                id="time"
                name="time"
                value={eventData.time}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="location">
              Location
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={eventData.location}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter event location"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="category">
              Category
            </label>
            <select
              id="category"
              name="category"
              value={eventData.category}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Category</option>
              <option value="Networking">Networking</option>
              <option value="Educational">Educational</option>
              <option value="Career">Career</option>
              <option value="Social">Social</option>
              <option value="Workshop">Workshop</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={eventData.description}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="4"
              placeholder="Enter event description"
            ></textarea>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="deadline">
              Registration Deadline
            </label>
            <input
              type="date"
              id="deadline"
              name="deadline"
              value={eventData.deadline}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="ticketPrice">
              Ticket Price
            </label>
            <input
              type="text"
              id="ticketPrice"
              name="ticketPrice"
              value={eventData.ticketPrice}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter ticket price or 'Free'"
            />
          </div>
          <div>
            <label className="flex items-center text-gray-700 text-sm font-bold">
              <input
                type="checkbox"
                name="virtual"
                checked={eventData.virtual}
                onChange={handleChange}
                className="mr-2"
              />
              Virtual Event
            </label>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="speakers">
              Speakers (comma-separated)
            </label>
            <input
              type="text"
              id="speakers"
              name="speakers"
              value={eventData.speakers}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. John Doe, Jane Smith"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="attendeeLimit">
              Attendee Limit
            </label>
            <input
              type="number"
              id="attendeeLimit"
              name="attendeeLimit"
              value={eventData.attendeeLimit}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter maximum number of attendees"
            />
          </div>
        </form>
      </div>
      <div className="p-6 bg-gray-100 flex justify-end space-x-4">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-300"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
        >
          Create Event
        </button>
      </div>
    </motion.div>
  );
};

const EventsPage = () => {
  const { user, canCreateEvent } = useAuth();
  const { events, addEvent, updateEvent } = useEvents();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [filters, setFilters] = useState({
    category: [],
    date: null,
  });
  const [filteredEvents, setFilteredEvents] = useState(sampleEvents);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    setFilteredEvents(sampleEvents);
  }, []);

  const handleCreateEvent = (newEvent) => {
    if (user) {
      const eventWithId = {
        ...newEvent,
        id: Date.now().toString(),
        attendees: 0,
        createdBy: user.id
      };
      addEvent(eventWithId);
      setCreateEventOpen(false);
      setFilteredEvents(prevEvents => [eventWithId, ...prevEvents]);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const filtered = sampleEvents.filter(event => 
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredEvents(filtered);
  };

  const handleFilterChange = (category, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [category]: prevFilters[category].includes(value)
        ? prevFilters[category].filter(item => item !== value)
        : [...prevFilters[category], value]
    }));
  };

  const handleDateChange = (e) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      date: e.target.value
    }));
  };

  const applyFilters = () => {
    let filtered = [...sampleEvents];
    if (filters.category.length > 0) {
      filtered = filtered.filter(event => filters.category.includes(event.category));
    }
    if (filters.date) {
      filtered = filtered.filter(event => event.date >= filters.date);
    }
    setFilteredEvents(filtered);
    setFilterOpen(false);
  };

  const handleRegister = (event) => {
    setSelectedEvent(event);
  };

  const handleRegistrationSubmit = (e) => {
    e.preventDefault();
    const registeredEvents = JSON.parse(localStorage.getItem('registeredEvents') || '[]');
    registeredEvents.push(selectedEvent.id);
    localStorage.setItem('registeredEvents', JSON.stringify(registeredEvents));
    setSelectedEvent(null);
    // Update the events state to reflect the registration
    setFilteredEvents(prevEvents => 
      prevEvents.map(event => 
        event.id === selectedEvent.id ? { ...event, attendees: event.attendees + 1 } : event
      )
    );
  };

  return (
    <>
     <header className="bg-gradient-to-r from-blue-600 to-indigo-800 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4">Events and Reunions</h1>
          <p className="text-xl max-w-2xl">Seamless access to networking, career opportunities, and alumni events through web and mobile apps will strengthen connections among alumni, fostering a vibrant and active community.</p>
        </div>
      </header>
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">

        <div className="mb-8 flex flex-wrap gap-4">
          <form onSubmit={handleSearch} className="flex-grow">
            <div className="relative">
              <input
                type="text"
                placeholder="Search events by title or description..."
                className="w-full p-4 pl-12 pr-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </form>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setFilterOpen(!filterOpen)}
            className="bg-blue-500 text-white px-6 py-3 rounded-lg flex items-center shadow-md hover:bg-blue-600 transition duration-300"
          >
            <FaFilter className="mr-2" /> Filters
          </motion.button>

          {canCreateEvent && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCreateEventOpen(true)}
              className="bg-green-500 text-white px-6 py-3 rounded-lg flex items-center shadow-md hover:bg-green-600 transition duration-300"
            >
              <FaPlus className="mr-2" /> Create Event
            </motion.button>
          )}
        </div>

        <AnimatePresence>
          {filterOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white p-6 rounded-lg shadow-md mb-8"
            >
              <h2 className="text-xl font-semibold mb-4">Filters</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">Category</h3>
                  {['Networking', 'Educational', 'Career', 'Social', 'Workshop'].map(category => (
                    <label key={category} className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        checked={filters.category.includes(category)}
                        onChange={() => handleFilterChange('category', category)}
                        className="mr-2"
                      />
                      {category}
                    </label>
                  ))}
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Date</h3>
                  <input
                    type="date"
                    value={filters.date || ''}
                    onChange={handleDateChange}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={applyFilters}
                  className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition duration-300"
                >
                  Apply Filters
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div>
          <AnimatePresence>
            {filteredEvents.map(event => (
              <EventCard key={event.id} event={event} onRegister={handleRegister} />
            ))}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {selectedEvent && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-white rounded-lg p-6 w-full max-w-md"
              >
                <h2 className="text-2xl font-bold mb-4">Register for {selectedEvent.title}</h2>
                <form onSubmit={handleRegistrationSubmit}>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setSelectedEvent(null)}
                      className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
                    >
                      Confirm Registration
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {createEventOpen && (
            <CreateEventForm
              onClose={() => setCreateEventOpen(false)}
              onSubmit={handleCreateEvent}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
    </>
  );
};

export default EventsPage;